import { type FormEvent, type KeyboardEvent, type MouseEvent, type TouchEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useThreadToWiki, useWikiPage, useWikiSpaces } from "../api/wiki";
import { wikiPagePath } from "../lib/wiki";
import { type LinkPreviewDTO, markdownToPlain, type MeDTO, type MessageDTO, type TaskCardDTO, type UserDTO, wikiTitleSchema } from "@justone/shared";
import { errorMessage, http } from "../api/http";
import { isPending, useDeleteMessage, useEditMessage, useToggleReaction, useUsers } from "../api/queries";
import { formatFull, formatTime, messagePath } from "../lib/format";
import { fromBody } from "../lib/mentionTags";
import { AttachmentList } from "./Attachments";
import { type Anchor, type MenuItem, MenuItems, Popover, PopoverMenu, useMenuKeys } from "./Popover";
import { MentionList, MentionTextarea, useMentionField } from "./MentionField";
import { MessageBody } from "./MessageBody";
import { UserButton } from "./Previews";
import { QuickAdd } from "./QuickAdd";
import { DueChip, PriorityFlag, TaskRefChip, useOpenTask } from "./TaskBits";
import { Avatar, Dialog, Field, Icon, useConfirm, useToast } from "./ui";
import { TASK_STATUS_NAMES } from "@justone/shared";

/** 빠른 리액션 */
export const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏", "🎉", "👀", "✅", "🔥", "👏", "💯"];

interface Props {
  message: MessageDTO;
  users: Map<number, UserDTO>;
  me: MeDTO;
  meNames: Set<string>;
  continued: boolean;
  /** 스레드 열기. 없으면 스레드 관련 표시를 하지 않는다 (스레드 창 안의 답글) */
  onOpenThread?: (rootId: number) => void;
  /** DM: 이 메시지부터 채널로 옮기기 */
  onMoveFrom?: (messageId: number) => void;
}

/** DM 삭제까지 남은 기간 */
function expiryLabel(iso: string): { text: string; soon: boolean } {
  const ms = new Date(iso).getTime() - Date.now();
  const days = Math.ceil(ms / 86_400_000);
  if (ms <= 3_600_000) return { text: "곧 삭제", soon: true };
  return days <= 1 ? { text: `${Math.max(1, Math.round(ms / 3_600_000))}시간 후 삭제`, soon: true } : { text: `${days}일 후 삭제`, soon: days <= 2 };
}

export function MessageRow({ message: m, users, me, meNames, continued, onOpenThread, onMoveFrom }: Props) {
  const [editing, setEditing] = useState(false);
  const [picker, setPicker] = useState(false);
  const [makingTask, setMakingTask] = useState(false);
  const [makingWiki, setMakingWiki] = useState(false);
  /** ⋯ 버튼이나 오른쪽 클릭한 자리 */
  const [menu, setMenu] = useState<Anchor | null>(null);
  /** 휴대폰: 길게 누르면 아래에서 올라오는 동작 시트 */
  const [sheet, setSheet] = useState(false);
  const pickerButton = useRef<HTMLButtonElement>(null);
  const pickerMenu = useRef<HTMLDivElement>(null);
  useMenuKeys(picker, pickerMenu);
  const author = m.authorId ? users.get(m.authorId) : undefined;
  const mine = m.authorId === me.id;
  const name = author?.displayName ?? "알 수 없는 사용자";
  const isUser = m.kind === "user" && !m.deleted;
  const isCard = m.kind === "task_card" && !m.deleted;
  // 리액션·스레드는 할일 카드에도 (카드 스레드 = 할일 댓글)
  // 보내는 중인 메시지는 아직 서버 id가 없어 리액션·메뉴를 쓸 수 없다
  const sending = isPending(m);
  const reactable = (isUser || isCard) && !sending;
  // DM에서는 할일을 만들 수 없다 (PRD 5.4)
  const canMakeTask = isUser && m.channelId !== null;
  // 스레드 → 위키: 채널의 원글에서 (DM은 동의 없이 공개되지 않게 하지 않는다)
  const canMakeWiki = isUser && m.channelId !== null && m.parentId === null;
  const canEdit = mine && isUser;
  // 관리자라도 남의 DM은 지울 수 없다
  const canDelete = isUser && (mine || (me.role === "admin" && m.conversationId === null));
  const canMove = !!onMoveFrom && isUser && m.parentId === null;
  const expiry = m.expiresAt ? expiryLabel(m.expiresAt) : null;
  const canThread = !!onOpenThread && reactable && m.parentId === null;
  const del = useDeleteMessage();
  const toggle = useToggleReaction(me.id);
  const toast = useToast();
  const confirm = useConfirm();

  const react = (emoji: string) => {
    setPicker(false);
    const on = !m.reactions.some((r) => r.emoji === emoji && r.userIds.includes(me.id));
    toggle.mutate({ message: m, emoji, on }, { onError: (err) => toast(errorMessage(err)) });
  };

  const onDelete = async () => {
    const ok = await confirm({ title: "메시지 삭제", body: mine ? "삭제하면 되돌릴 수 없습니다. 스레드 답글은 남습니다." : `${name}님의 메시지를 관리자로서 삭제합니다. 되돌릴 수 없습니다.`, confirmLabel: "삭제", danger: true });
    if (ok) del.mutate(m.id, { onError: (err) => toast(errorMessage(err)) });
  };

  const copy = (text: string, done: string) =>
    void navigator.clipboard?.writeText(text).then(
      () => toast(done),
      () => toast("복사하지 못했습니다."),
    );
  const actions: MenuItem[] = [
    { label: "링크 복사", onSelect: () => copy(`${location.origin}${messagePath(m, m.id, m.parentId)}`, "메시지 링크를 복사했습니다.") },
    ...(canMakeTask ? [{ label: "할일로 만들기", onSelect: () => setMakingTask(true) }] : []),
    ...(canMakeWiki ? [{ label: "위키로 정리", onSelect: () => setMakingWiki(true) }] : []),
    ...(canMove ? [{ label: "채널로 옮기기", onSelect: () => onMoveFrom!(m.id) }] : []),
    ...(canEdit ? [{ label: "수정", onSelect: () => setEditing(true), separated: true }] : []),
    ...(canDelete ? [{ label: "삭제", onSelect: () => void onDelete(), danger: true, separated: !canEdit }] : []),
  ];

  // 오른쪽 클릭(휴대폰은 길게 누르기)으로 메뉴. 링크·사진·고른 글자 위에서는 브라우저 메뉴를 그대로 둔다
  const coarse = () => window.matchMedia("(pointer: coarse)").matches;
  const onContextMenu = (e: MouseEvent) => {
    if (!reactable || editing) return;
    const t = e.target as HTMLElement;
    if (t.closest("a, img, video, input, textarea, .msg-tools") || String(window.getSelection() ?? "").trim()) return;
    e.preventDefault();
    if (coarse()) setSheet(true);
    else setMenu({ x: e.clientX, y: e.clientY });
  };
  const press = useRef<{ timer: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null);
  /** 길게 눌러 시트를 연 직후의 손 떼기는 누르기로 치지 않는다 (시트의 항목이 눌리지 않게) */
  const longPressed = useRef(false);
  const onTouchStart = (e: TouchEvent) => {
    if (!reactable || editing || e.touches.length !== 1) return;
    const { clientX: x, clientY: y } = e.touches[0]!;
    press.current = {
      x,
      y,
      timer: setTimeout(() => {
        press.current = null;
        longPressed.current = true;
        navigator.vibrate?.(10);
        setSheet(true);
      }, 450),
    };
  };
  const cancelPress = (e?: TouchEvent) => {
    const p = press.current;
    if (!p) return;
    if (e?.type === "touchmove") {
      const t = e.touches[0]!;
      if (Math.hypot(t.clientX - p.x, t.clientY - p.y) < 10) return;
    }
    clearTimeout(p.timer);
    press.current = null;
  };

  const cls = ["msg", continued ? "cont" : "first", m.kind === "system" ? "system" : "", m.deleted ? "deleted" : "", picker || menu ? "picking" : "", sending ? "sending" : ""].filter(Boolean).join(" ");
  return (
    <div className={cls} data-message-id={m.id} onContextMenu={onContextMenu} onTouchStart={onTouchStart} onTouchMove={cancelPress} onTouchEnd={(e) => {
        cancelPress();
        if (longPressed.current) {
          longPressed.current = false;
          e.preventDefault();
        }
      }} onTouchCancel={() => cancelPress()}>
      {/* 도구 모음은 마우스를 올렸을 때만 보이므로 대화상자는 그 밖에 둔다 */}
      {makingTask && <MakeTaskDialog message={m} onClose={() => setMakingTask(false)} />}
      {makingWiki && <ThreadToWikiDialog message={m} onClose={() => setMakingWiki(false)} />}
      {continued ? (
        <time className="gutter" dateTime={m.createdAt} title={formatFull(m.createdAt)}>
          {formatTime(m.createdAt)}
        </time>
      ) : (
        <UserButton user={author} className="msg-avatar" label={`${name} 정보`}>
          <Avatar user={author} size={40} />
        </UserButton>
      )}
      <div className="msg-main">
        {!continued && (
          <div className="msg-head">
            <UserButton user={author} className="author">
              {name}
            </UserButton>
            {author && !author.active && <span className="pill">비활성</span>}
            <time dateTime={m.createdAt} title={formatFull(m.createdAt)}>
              {formatTime(m.createdAt)}
            </time>
            {sending && <span className="sending-tag">보내는 중…</span>}
            {expiry && <span className={`expiry${expiry.soon ? " soon" : ""}`}>{expiry.text}</span>}
          </div>
        )}
        {m.kind === "moved" && <div className="moved-label">DM에서 옮겨온 대화</div>}
        {m.parentId !== null && m.alsoInChannel && onOpenThread && (
          <button type="button" className="thread-origin" onClick={() => onOpenThread(m.parentId!)}>
            스레드에 남긴 답글
          </button>
        )}
        {editing ? (
          <EditBox message={m} onDone={() => setEditing(false)} />
        ) : m.deleted ? (
          <div className="text">삭제된 메시지입니다.</div>
        ) : m.kind === "moved" ? (
          <div className="text moved-body">
            <MessageBody body={m.body} me={meNames} channelId={m.channelId} />
            {m.attachments.length > 0 && <AttachmentList attachments={m.attachments} />}
          </div>
        ) : m.kind === "system" ? (
          <div className="text">{m.body}</div>
        ) : m.kind === "task_card" && m.task ? (
          <TaskCardBlock card={m.task} users={users} />
        ) : m.kind === "wiki_card" && m.wiki ? (
          <WikiCardBlock wiki={m.wiki} fromThread={m.parentId !== null} />
        ) : (
          <div className="text">
            {m.body && <MessageBody body={m.body} me={meNames} channelId={m.channelId} />}
            {m.editedAt && (
              <span className="edited" title={formatFull(m.editedAt)}>
                {" "}
                (수정됨)
              </span>
            )}
            {m.attachments.length > 0 && <AttachmentList attachments={m.attachments} />}
            {m.taskIds.length > 0 && (
              <span className="task-badges">
                {m.taskIds.map((id) => (
                  <Link key={id} to={{ search: `?task=${id}` }} className="chip chip-task" title="이 메시지에서 만든 할일">
                    → T-{id}
                  </Link>
                ))}
              </span>
            )}
            <InternalLinkCards body={m.body} />
            {m.previews.length > 0 && <LinkPreviews message={m} mine={mine} />}
          </div>
        )}
        {m.reactions.length > 0 && (
          <div className="reactions">
            {m.reactions.map((r) => {
              const pressed = r.userIds.includes(me.id);
              const who = r.userIds.map((id) => users.get(id)?.displayName ?? "알 수 없음").join(", ");
              return (
                <button key={r.emoji} type="button" className={`reaction${pressed ? " mine" : ""}`} aria-pressed={pressed} title={`${who}님이 ${r.emoji}`} onClick={() => react(r.emoji)}>
                  <span className="emoji">{r.emoji}</span>
                  <span className="count">{r.userIds.length}</span>
                </button>
              );
            })}
          </div>
        )}
        {canThread && m.replyCount > 0 && (
          <button type="button" className="thread-summary" onClick={() => onOpenThread!(m.id)}>
            <span className="faces">
              {m.replyUserIds.map((id) => (
                <Avatar key={id} user={users.get(id)} size={20} />
              ))}
            </span>
            <span className="count">답글 {m.replyCount}개</span>
            {m.lastReplyAt && <span className="last">마지막 답글 {formatTime(m.lastReplyAt)}</span>}
          </button>
        )}
      </div>
      {reactable && !editing && (
        <div className="msg-tools">
          <button ref={pickerButton} type="button" onClick={() => setPicker((p) => !p)} aria-label="리액션 달기" aria-haspopup="menu" aria-expanded={picker}>
            <Icon name="smile" />
          </button>
          {canThread && (
            <button type="button" onClick={() => onOpenThread!(m.id)} aria-label="스레드에 답하기">
              <Icon name="reply" />
            </button>
          )}
          <button type="button" onClick={(e) => setMenu(menu ? null : e.currentTarget)} aria-label="메시지 메뉴" data-tip="더 보기 (오른쪽 클릭)" aria-haspopup="menu" aria-expanded={!!menu}>
            <Icon name="more" />
          </button>
        </div>
      )}
      <Popover anchor={pickerButton.current} open={picker} onClose={() => setPicker(false)} placement="bottom-end" className="emoji-popover" role="presentation">
        <div ref={pickerMenu} className="emoji-picker" role="menu" aria-label="리액션 고르기">
          {QUICK_EMOJIS.map((e) => (
            <button key={e} type="button" role="menuitem" tabIndex={-1} onClick={() => react(e)} aria-label={e}>
              {e}
            </button>
          ))}
        </div>
      </Popover>
      <PopoverMenu anchor={menu} open={!!menu} onClose={() => setMenu(null)} items={actions} label="메시지 메뉴" />
      {sheet && (
        <Dialog open onClose={() => setSheet(false)} label="메시지 동작" className="sheet">
          <div className="sheet-handle" aria-hidden="true" />
          <div className="sheet-reactions" role="group" aria-label="리액션">
            {QUICK_EMOJIS.slice(0, 6).map((e) => (
              <button
                key={e}
                type="button"
                aria-label={e}
                className={m.reactions.some((r) => r.emoji === e && r.userIds.includes(me.id)) ? "on" : undefined}
                onClick={() => {
                  setSheet(false);
                  react(e);
                }}
              >
                {e}
              </button>
            ))}
          </div>
          <div className="sheet-menu" role="menu" aria-label="메시지 동작">
            <MenuItems
              items={[
                ...(canThread ? [{ label: "스레드에 답하기", onSelect: () => onOpenThread!(m.id) }] : []),
                ...(m.body ? [{ label: "글 복사", onSelect: () => copy(m.body, "메시지 글을 복사했습니다.") }] : []),
                ...actions,
              ]}
              onClose={() => setSheet(false)}
            />
          </div>
        </Dialog>
      )}
    </div>
  );
}

function EditBox({ message, onDone }: { message: MessageDTO; onDone: () => void }) {
  const { data: users = [] } = useUsers();
  // 이미 쓴 "@kernel"도 고칠 때는 "@황승현 (kernel)" 태그로
  const field = useMentionField(fromBody(message.body, users));
  // 맨 아래 메시지를 고칠 때 입력창에 가려지지 않게
  useEffect(() => {
    void field.ref.current?.scrollIntoView({ block: "nearest" });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- 열 때만
  const edit = useEditMessage();
  const toast = useToast();
  const save = () => {
    const body = field.body().trim();
    if (!body || body === message.body) return onDone();
    edit.mutate({ id: message.id, body }, { onSuccess: onDone, onError: (err) => toast(errorMessage(err)) });
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (field.onKeyDown(e)) return;
    if (e.key === "Escape") onDone();
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) {
      e.preventDefault();
      save();
    }
  };
  return (
    <div className="msg-edit">
      <label htmlFor={`edit-${message.id}`} className="sr-only">
        메시지 수정
      </label>
      <MentionList field={field} />
      <MentionTextarea field={field} id={`edit-${message.id}`} className="input" onKeyDown={onKeyDown} autoFocus rows={1} />
      <div className="row">
        <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={edit.isPending}>
          저장
        </button>
        <button type="button" className="btn btn-sm" onClick={onDone}>
          취소
        </button>
        <span>Enter로 저장, Esc로 취소</span>
      </div>
    </div>
  );
}

/** 채널의 할일 카드: 누르면 할일 상세 */
function TaskCardBlock({ card, users }: { card: TaskCardDTO; users: Map<number, UserDTO> }) {
  const open = useOpenTask();
  if (card.deleted)
    return (
      <div className="task-card deleted">
        <span className="task-no">T-{card.id}</span> 지운 할일
      </div>
    );
  const assignee = card.assigneeId !== null ? users.get(card.assigneeId) : undefined;
  return (
    <button type="button" className={`task-card st-${card.status}`} onClick={() => open(card.id)}>
      <span className="task-card-top">
        <span className="task-no">T-{card.id}</span>
        <span className={`status-pill st-${card.status}`}>{TASK_STATUS_NAMES[card.status]}</span>
        <PriorityFlag priority={card.priority} />
      </span>
      <span className={`task-card-title${card.status === "done" ? " is-done" : ""}`}>{card.title}</span>
      <span className="task-meta">
        {assignee ? (
          <span className="who">
            <Avatar user={assignee} size={18} />
            {assignee.displayName}
          </span>
        ) : (
          <span className="muted">담당자 없음</span>
        )}
        <DueChip task={card} />
        {card.subtaskCount > 0 && (
          <span className="subcount" title="하위 할일">
            {card.subtaskDone}/{card.subtaskCount}
          </span>
        )}
      </span>
    </button>
  );
}

/** 메시지 → 할일 (PRD 6.5): 제목은 메시지 앞부분, 기본은 이 채널의 팀 할일 */
function MakeTaskDialog({ message, onClose }: { message: MessageDTO; onClose: () => void }) {
  const first = message.body.split("\n").find((l) => l.trim()) ?? "";
  const initial = first.replace(/[*_~`>#]/g, "").trim().slice(0, 100);
  return (
    <Dialog open onClose={onClose} label="할일로 만들기">
      <div className="dialog-body">
        <h2>할일로 만들기</h2>
        <QuickAdd defaults={{ channelId: message.channelId! }} initialText={initial} sourceMessageId={message.id} allowPersonal autoFocus onDone={onClose} onCancel={onClose} />
      </div>
    </Dialog>
  );
}

/** 스레드 → 위키 (PRD 7.8): 그 채널 위키에 내 초안으로 만들고 연다 */
function ThreadToWikiDialog({ message, onClose }: { message: MessageDTO; onClose: () => void }) {
  const first = message.body.split("\n").find((l) => l.trim()) ?? "";
  const [title, setTitle] = useState(() => first.replace(/[*_~`>#|[\]]/g, "").replace(/\s+/g, " ").trim().slice(0, 60) || "스레드 정리");
  const [err, setErr] = useState<string | null>(null);
  const make = useThreadToWiki();
  const navigate = useNavigate();
  const replies = message.replyCount;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const checked = wikiTitleSchema.safeParse(title);
    if (!checked.success) return setErr(checked.error.issues[0]?.message ?? "제목을 확인하세요.");
    make.mutate(
      { messageId: message.id, title: checked.data },
      {
        onSuccess: (p) => {
          onClose();
          navigate(wikiPagePath(message.channelId, p.id));
        },
        onError: (e) => setErr(errorMessage(e)),
      },
    );
  };
  return (
    <Dialog open onClose={onClose} label="위키로 정리">
      <form onSubmit={submit}>
        <h2>위키로 정리</h2>
        <p className="muted">
          {replies > 0 ? `원글과 답글 ${replies}개를` : "이 메시지를"} 이 채널 위키의 내 초안으로 옮깁니다. 원문 스레드 링크가 함께 들어가고, 게시하기 전에는 나만 봅니다. 게시하면 스레드에 페이지 카드가 달립니다.
        </p>
        <Field id="thread-wiki-title" label="페이지 제목" error={err ?? undefined}>
          <input id="thread-wiki-title" className="input" required maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus autoComplete="off" />
        </Field>
        <div className="buttons">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={make.isPending}>
            초안 만들기
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** 외부 링크 미리보기 (서버가 가져와 둔 것). 보낸 사람은 숨길 수 있다 */
function LinkPreviews({ message, mine }: { message: MessageDTO; mine: boolean }) {
  const toast = useToast();
  const hide = () => void http.delete(`/api/messages/${message.id}/previews`).catch((e) => toast(errorMessage(e)));
  return (
    <div className="link-previews">
      {message.previews.map((p) => (
        <LinkPreviewCard key={p.id} preview={p} />
      ))}
      {mine && (
        <button type="button" className="lp-hide" onClick={hide} title="이 메시지의 링크 미리보기 숨기기" aria-label="링크 미리보기 숨기기">
          <Icon name="close" />
        </button>
      )}
    </div>
  );
}

function LinkPreviewCard({ preview: p }: { preview: LinkPreviewDTO }) {
  const [broken, setBroken] = useState(false);
  return (
    <a className="link-preview" href={p.url} target="_blank" rel="noopener noreferrer">
      <span className="lp-text">
        <span className="lp-site">{p.siteName}</span>
        {p.title && <span className="lp-title">{p.title}</span>}
        {p.description && <span className="lp-desc">{p.description}</span>}
      </span>
      {p.imageUrl && !broken && <img className="lp-image" src={p.imageUrl} width={p.imageWidth ?? undefined} height={p.imageHeight ?? undefined} alt="" loading="lazy" onError={() => setBroken(true)} />}
    </a>
  );
}

/** 이 서비스의 위키·할일 주소를 붙여 넣으면 카드로 (PRD 4.2 내부 링크 미리보기) */
function internalLinks(body: string): { kind: "wiki" | "task"; id: number }[] {
  const out: { kind: "wiki" | "task"; id: number }[] = [];
  for (const raw of body.match(/https?:\/\/[^\s<>()]+/g) ?? []) {
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      continue;
    }
    if (url.origin !== location.origin) continue;
    const task = Number(url.searchParams.get("task"));
    const wiki = /^\/(?:c\/\d+\/)?wiki\/(\d+)/.exec(url.pathname)?.[1];
    const item = task > 0 ? { kind: "task" as const, id: task } : wiki ? { kind: "wiki" as const, id: Number(wiki) } : null;
    if (item && !out.some((x) => x.kind === item.kind && x.id === item.id)) out.push(item);
  }
  return out.slice(0, 3);
}

function InternalLinkCards({ body }: { body: string }) {
  const links = internalLinks(body);
  if (!links.length) return null;
  return (
    <div className="link-previews">
      {links.map((l) => (l.kind === "task" ? <div key={`t${l.id}`} className="link-preview internal"><TaskRefChip id={l.id} /></div> : <WikiLinkCard key={`w${l.id}`} pageId={l.id} />))}
    </div>
  );
}

function WikiLinkCard({ pageId }: { pageId: number }) {
  const { data: page } = useWikiPage(pageId);
  const { data: spaces = [] } = useWikiSpaces();
  if (!page || page.deleted) return null;
  const space = spaces.find((s) => s.id === page.spaceId);
  const text = markdownToPlain(page.markdown).slice(0, 140);
  return (
    <Link className="link-preview internal" to={wikiPagePath(space?.channelId ?? null, page.id)}>
      <span className="lp-text">
        <span className="lp-site">위키 · {space?.name ?? ""}</span>
        <span className="lp-title">{page.title}</span>
        {text && <span className="lp-desc">{text}</span>}
      </span>
    </Link>
  );
}

/** 위키 페이지 게시 알림 카드 (스레드 안이면 그 스레드를 정리한 페이지) */
function WikiCardBlock({ wiki, fromThread }: { wiki: NonNullable<MessageDTO["wiki"]>; fromThread: boolean }) {
  const { data: spaces = [] } = useWikiSpaces();
  const channelId = spaces.find((s) => s.id === wiki.spaceId)?.channelId ?? null;
  if (wiki.deleted)
    return (
      <div className="task-card deleted">
        <span className="pill">위키</span> 삭제된 페이지
      </div>
    );
  return (
    <Link className="task-card wiki-card" to={wikiPagePath(channelId, wiki.pageId)}>
      <span className="task-card-top">
        <span className="pill">위키</span>
        <span className="muted">{fromThread ? "이 스레드를 위키로 정리했습니다" : "새 페이지를 게시했습니다"}</span>
      </span>
      <span className="task-card-title">{wiki.title}</span>
    </Link>
  );
}
